import STATIC_LANGUAGE_ALTERNATES from "../data/static-language-alternates.json"

export const LANGUAGE_STORAGE_KEY = `marcinstanek-language`
export const ENGLISH_HOME_PATH = `/en/`
export { STATIC_LANGUAGE_ALTERNATES }

const CRAWLER_UA_SOURCE = `googlebot|bingbot|slurp|duckduckbot|baiduspider|yandex|facebookexternalhit|twitterbot|linkedinbot|embedly|pinterest|redditbot|applebot|semrush|ahrefs|dotbot|mj12bot|petalbot|bytespider|gptbot|chatgpt|claudebot|anthropic|ccbot|bingpreview|whatsapp|telegrambot|slackbot|discordbot|ia_archiver|lighthouse|storebot-google|google-inspectiontool|google-extended`
const CRAWLER_UA = new RegExp(CRAWLER_UA_SOURCE, `i`)

export function normalizePathname(pathname) {
  if (!pathname) return `/`
  let value = String(pathname).trim()
  if (!value) return `/`
  if (/^https?:\/\//i.test(value)) {
    try {
      value = new URL(value).pathname
    } catch {
      return `/`
    }
  }
  if (!value.startsWith(`/`)) value = `/${value}`
  if (value !== `/` && !value.endsWith(`/`)) value = `${value}/`
  return value
}

export function isPolishHomePath(pathname) {
  const path = normalizePathname(pathname)
  return path === `/`
}

export function pathLanguage(pathname) {
  const path = normalizePathname(pathname)
  if (
    path === `/en/` ||
    path.startsWith(`/en/`) ||
    path.startsWith(`/blog/en/`)
  ) {
    return `en`
  }
  return `pl`
}

export function hasPolishBrowserLanguage(languages) {
  if (!languages || !languages.length) return false
  return languages.some(lang => /^pl([-_]|$)/i.test(String(lang || ``).trim()))
}

export function isLikelyCrawler(userAgent) {
  if (!userAgent) return false
  return CRAWLER_UA.test(String(userAgent))
}

export function desiredSiteLanguage({
  storedPreference,
  languages,
  userAgent,
}) {
  if (isLikelyCrawler(userAgent)) return null
  if (storedPreference === `pl` || storedPreference === `en`) {
    return storedPreference
  }
  return hasPolishBrowserLanguage(languages) ? `pl` : `en`
}

function languageAlternateMap(overrides) {
  if (overrides) return overrides
  if (
    typeof window !== `undefined` &&
    window.__LANGUAGE_ALTERNATES__ &&
    typeof window.__LANGUAGE_ALTERNATES__ === `object`
  ) {
    return window.__LANGUAGE_ALTERNATES__
  }
  return STATIC_LANGUAGE_ALTERNATES
}

export function languageRedirect({
  pathname,
  storedPreference,
  languages,
  userAgent,
  alternateHref,
  alternates,
}) {
  const desired = desiredSiteLanguage({
    storedPreference,
    languages,
    userAgent,
  })
  if (!desired) return null
  const currentPath = normalizePathname(pathname)
  if (pathLanguage(currentPath) === desired) return null
  const fromAlternate = alternateHref ? normalizePathname(alternateHref) : ``
  const fromMap = languageAlternateMap(alternates)[currentPath]
  const target = fromAlternate || fromMap
  if (!target) return null
  if (pathLanguage(target) !== desired) return null
  if (target === currentPath) return null
  return target
}

export function homepageLanguageRedirect(args) {
  return languageRedirect(args)
}

export function readStoredLanguage() {
  if (typeof window === `undefined`) return null
  try {
    const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
    return value === `pl` || value === `en` ? value : null
  } catch {
    return null
  }
}

export function writeStoredLanguage(lang) {
  if (typeof window === `undefined`) return
  if (lang !== `pl` && lang !== `en`) return
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang)
  } catch {
    // Ignore quota / private-mode failures.
  }
}

function alternateHrefFromDocument(desired) {
  if (!desired || typeof document === `undefined`) return undefined
  const link = document.querySelector(
    `link[rel="alternate"][hreflang="${desired}"]`
  )
  return link?.getAttribute(`href`) || undefined
}

export function applyHomepageLanguageRedirect(location) {
  if (typeof window === `undefined`) return
  const pathname = location?.pathname ?? window.location.pathname
  const languages =
    typeof navigator !== `undefined` &&
    navigator.languages &&
    navigator.languages.length
      ? Array.from(navigator.languages)
      : typeof navigator !== `undefined`
      ? [navigator.language]
      : []
  const userAgent = typeof navigator !== `undefined` ? navigator.userAgent : ``
  const storedPreference = readStoredLanguage()
  const desired = desiredSiteLanguage({
    storedPreference,
    languages,
    userAgent,
  })
  const target = languageRedirect({
    pathname,
    storedPreference,
    languages,
    userAgent,
    alternateHref: alternateHrefFromDocument(desired),
  })
  if (!target) return
  window.location.replace(target)
}

export function homepageLanguageInlineScript(
  alternates = STATIC_LANGUAGE_ALTERNATES
) {
  return `(function(){try{var STATIC=${JSON.stringify(
    alternates
  )};window.__LANGUAGE_ALTERNATES__=STATIC;var ua=navigator.userAgent||"";if(/${CRAWLER_UA_SOURCE}/i.test(ua))return;var s=null;try{s=localStorage.getItem("${LANGUAGE_STORAGE_KEY}")}catch(e){}var desired;if(s==="pl"||s==="en")desired=s;else{var langs=navigator.languages&&navigator.languages.length?navigator.languages:[navigator.language];var isPl=false;for(var i=0;i<langs.length;i++){if(/^pl([-_]|$)/i.test(String(langs[i]||"").trim())){isPl=true;break}}desired=isPl?"pl":"en"}var p=location.pathname||"/";if(p!=="/"&&p.charAt(p.length-1)!=="/")p+="/";var current=(p==="/en/"||p.indexOf("/en/")===0||p.indexOf("/blog/en/")===0)?"en":"pl";if(desired===current)return;var target=STATIC[p];if(!target&&typeof document!=="undefined"){var link=document.querySelector('link[rel="alternate"][hreflang="'+desired+'"]');var href=link&&link.getAttribute("href");if(href){if(href.indexOf("http")===0){try{target=new URL(href).pathname}catch(e){target=null}}else target=href;if(target&&target!=="/"&&target.charAt(target.length-1)!=="/")target+="/"}}if(target&&target!==p)location.replace(target)}catch(e){}})();`
}
